import React, { FC, useState, useEffect } from 'react'
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/solid'
import { Link } from 'react-router-dom'
import { StyledSubscriptionCard } from '..'
import { SubInfoType } from '../../../types'
import { useWindowSize } from '../../utils'
import ROUTES from '../../utils/routes'
import { TRIAL, YEAR, MONTH } from '../../utils/stripePrices'
import { PricingType } from '../../../redux/pricing/types'
import useSegment from '../../utils/useSegment'

type Props = {
  title: string;
  plan?: PricingType;
  interval?: string;
  description: string;
  userSub?: SubInfoType | null;
  isOwned?: boolean;
  mostPopular?: boolean;
  initialLength?: number;
  features?: Array<string>;
  isOpen: boolean;
  toggleOpen: () => void;
  setShowCancelModal: (showCancelModal) => void;
}

const SubscriptionCard: FC<Props> = ({
  title,
  plan,
  interval,
  description,
  userSub,
  isOwned,
  mostPopular,
  initialLength,
  features,
  toggleOpen,
  isOpen,
  setShowCancelModal
}) => {
  const [details, setDetails] = useState(features?.slice(0, initialLength))
  const [cardOpen, setCardOpen] = useState<boolean>(isOpen)

  const { breakpoints } = useWindowSize()
  const { track } = useSegment()
  const ownsAnnual = userSub?.plan?.interval === YEAR
  const isTrialUser = userSub?.plan?.interval === TRIAL && userSub?.status === 'active'
  const isNoSale = !plan?.sale

  const handleShowCancelModal = () => {
    setShowCancelModal(true)
    track('Clicked Unsubscribe')
  }

  // conditionally hide or show features based on card opening/closing
  useEffect(() => {
    if (cardOpen) {
      setDetails(features)
      return
    }

    setDetails(features?.slice(0, initialLength))
  }, [cardOpen])

  useEffect(() => {
    setCardOpen(isOpen)
  }, [isOpen])

  const handleDetails = (e) => {
    const isDesktop =
      breakpoints.includes('l') || breakpoints.includes('xl') || breakpoints.includes('m')

    if (isDesktop) {
      toggleOpen()
    }
    else {
      setCardOpen(!cardOpen)
    }
  }

  return (
    <StyledSubscriptionCard plan={plan?.interval} hasDiscount={isTrialUser && isNoSale}>
      <div className="subscription-card">
        <div className="card-gradient" />
        <div className="card-content">
          {mostPopular && <p className="most-popular">Most Popular</p>}
          <div className="card-header">
            <div className="card-header-main">
              <p className="card-title">{title}</p>
              <div>
                <p className="price">
                  <span className="original-price">
                    ${plan?.sale?.valueAfterDiscount || plan?.price}
                  </span>
                  {interval && (
                    <span className="interval">
                      /{plan?.sale && 'first '}
                      {interval}
                    </span>
                  )}
                  {plan?.sale && <span className="slashed-price">${plan?.price}</span>}
                </p>
                {plan?.sale && (
                  <span className="price-sale-description">Billed ${plan?.price} after</span>
                )}
              </div>
            </div>
            {isTrialUser && isNoSale && (
              <p className="discounted price">
                {plan?.interval === MONTH ? '$34' : '$449'}
                <span className="interval"> for first {interval}</span>
              </p>
            )}
          </div>
          <p className="subscription-description">{description}</p>
          <ul>
            {details?.map((details) => (
              <li key={details}>
                <CheckCircleIcon />
                {details}
              </li>
            ))}
          </ul>
          <div className="button-container">
            {!cardOpen ? (
              <button className="read-more" onClick={(e) => handleDetails(e)}>
                Read More
                <ChevronDownIcon />
              </button>
            ) : (
              <button className="read-more" onClick={(e) => handleDetails(e)}>
                Read Less
                <ChevronUpIcon />
              </button>
            )}
            {!isOwned && (!ownsAnnual || !userSub?.renews) && (
              <Link
                to={{
                  pathname: ROUTES.user.settings.payment,
                  state: {
                    plan: {
                      ...plan,
                      sale: {
                        ...plan?.sale,
                        startDate: undefined,
                        endDate: undefined
                      }
                    }
                  }
                }}
                className="select"
              >
                Select Plan
              </Link>
            )}
          </div>
        </div>
      </div>
      {isOwned && userSub?.renews && (
        <div className="button-container">
          <button className="unsubscribe" onClick={() => handleShowCancelModal()}>
            Unsubscribe
          </button>
        </div>
      )}
    </StyledSubscriptionCard>
  )
}

export default SubscriptionCard
